"use client";

import { useFeatureFlagEnabled } from "posthog-js/react";

import { ONBOARDING_INTERVIEW_FLAG } from "@/components/onboarding/onboarding-progress";
import { useMounted } from "@/hooks/use-mounted";

const JOIN_STEPS = ["phone", "sources", "profile", "interview"] as const;

export type JoinStep = (typeof JOIN_STEPS)[number];

// Single source of truth for the /join progress indicator. Shown identically
// on every step so the journey reads as Phone → Sources → Profile → Interview.
// Phone sub-states (QR vs Twilio code) do NOT advance this; they're one step.
//
// The interview is last and only counted for experts inside its rollout, which
// is why the flag is read here rather than passed in by each step: four call
// sites answering the same question separately is four chances to disagree,
// and a bar that says three on one screen and four on the next is worse than
// either. Before it was counted at all, the bar filled completely on the
// profile step and then a fourth screen appeared, promising an ending that was
// not the ending.
export function JoinStepBar({ active }: { active: JoinStep }) {
  const isMounted = useMounted();
  const interviewEnabled = useFeatureFlagEnabled(ONBOARDING_INTERVIEW_FLAG);
  const steps = interviewEnabled ? JOIN_STEPS : JOIN_STEPS.slice(0, -1);
  const activeIdx = steps.indexOf(active);

  // The flag resolves on the client only, so the server always renders the
  // shorter bar and an expert inside the rollout would hydrate into a longer
  // one. Waiting for the first post-mount commit costs a frame of no bar and
  // buys a segment count that never has to be corrected in place.
  if (!isMounted) {
    return null;
  }

  // Reachable by direct link: the interview route is deliberately not gated, so
  // someone outside the rollout can open it. Drawing a bar whose active step is
  // not on it would leave every segment dim and count a journey they are not in.
  if (activeIdx === -1) {
    return null;
  }

  return (
    <div className="fixed top-10 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
      {steps.map((step, i) => (
        <div
          key={step}
          className={`h-1 rounded-full transition-[width,background-color] duration-250 ${
            i === activeIdx
              ? "bg-marketing-blue w-16"
              : i < activeIdx
                ? "bg-marketing-blue w-[22px]"
                : "w-[22px] bg-neutral-300 dark:bg-neutral-700"
          }`}
        />
      ))}
    </div>
  );
}
